var i18n = require('i18n')
var moment = require('moment')
var _ = require('lodash')
var t = i18n.t
var InfiniteScrollController = require('../mixins/infinite-scroll-controller')
var datePeriodToRange = require('../utils/date-period-to-range')
var NOTIFICATION_KEYS = require('../notificationKeys')
var Scope = require('../utils/scope')
var BankLineProvider = require('../constants/bankLineProvider')
var BANK_MATCH_UI_FLOW = require('../constants/bank-match-ui-flow')
var getBankMatchTrackingHeaders = require('../utils/get-bank-match-tracking-headers')

module.exports = Em.Controller.extend(InfiniteScrollController, {
    auth: Em.inject.service(),

    needs: ['organization', 'bankSync', 'bankSyncPossibleSubjects', 'nextVoucherNo'],

    account: Em.computed.alias('controllers.bankSync.model'),

    organization: Em.computed.oneWay('controllers.organization.model'),

    showReconciledTransactions: Em.computed.alias('controllers.bankSync.showReconciledTransactions'),

    searchQuery: Em.computed.alias('controllers.bankSync.searchQuery'),

    hasBankIntegration: Em.computed.alias('controllers.bankSync.hasBankIntegration'),

    period: Em.computed.alias('controllers.bankSync.period'),

    sortProperty: Em.computed.alias('controllers.bankSync.sortProperty'),

    sortDirection: Em.computed.alias('controllers.bankSync.sortDirection'),

    transactionType: Em.computed.alias('controllers.bankSync.transactionType'),

    bankConnectionEstablished: Em.computed.alias('controllers.bankSync.bankConnectionEstablished'),

    showNoBankLinesToReconcileEmptyView: Em.computed.alias('controllers.bankSync.showNoBankLinesToReconcileEmptyView'),

    hasPartnerBankConnection: Em.computed.alias('controllers.bankSync.hasPartnerBankConnection'),

    bankIntegrations: Em.computed.alias('model'),

    bankConnections: Ember.inject.service(),

    bankAccounts: Ember.inject.service(),

    pollingInterval: 5000,

    _pollingTimer: null,

    _isPollingActive: false,

    _lastTotalCount: -1,

    _pollStartTime: null,

    _maxPollDuration: 5 * 60000, // 5 minutes

    init: function() {
        this._super()
        this.set('selectedLines', [])
    },

    isLoaded: false,

    remoteHasMore: false,

    _isInitialLoad: false,

    accountId: function() {
        return this.get('account.id')
    }.property('account'),

    organizationId: function() {
        return this.get('organization.id')
    }.property('organization'),

    bankLinesIds: null,

    matchSuggestions: null,

    matchSuggestionsJson: function() {
        var matchSuggestions = this.get('matchSuggestions') || []
        var allBankLines = this.get('content') || []

        var bankLinesWithSubjectAssociations = new Set()
        allBankLines.forEach(function(match) {
            var subjectAssociations = match.get('subjectAssociations.model.ids') || []
            var hasSubjectAssociations = subjectAssociations.length > 0
            if (hasSubjectAssociations) {
                bankLinesWithSubjectAssociations.add(match.get('id'))
            }
        })

        var filteredMatchSuggestions = matchSuggestions.filter(function(suggestion) {
            var matchId = suggestion.transactions[0].match_id || ''
            if (bankLinesWithSubjectAssociations.has(matchId)) {
                return false
            }
            var isCrossCurrency = suggestion.transactions[0].currency !== suggestion.reconcilables[0].currency
            var transactionAmount = Math.abs(suggestion.transactions[0].amount)
            var reconcilableAmount = Math.abs(suggestion.reconcilables[0].amount)
            var hasDifference = !isCrossCurrency && transactionAmount !== reconcilableAmount
            return !hasDifference
        })

        return JSON.stringify(filteredMatchSuggestions) || ''
    }.property('matchSuggestions'),

    areMatchesLoaded: function() {
        // passing booleans to web component doesn't work, so we convert to string
        return (this.get('isLoaded') || this.get('matchSuggestions') !== null) ? 'true' : 'false'
    }.property('isLoaded', 'matchSuggestions'),

    segment: Em.inject.service(),

    _lastLoadRequestId: 0,

    shouldShowConnectionEstablishedView: function() {
        return this.get('bankConnectionEstablished') === 'true' || this.get('_isPollingActive')
    }.property('bankConnectionEstablished', '_isPollingActive'),

    shouldShowEmptyView: function() {
        return this.get('isLoaded') && !this.get('_isPollingActive') && !this.get('bankConnectionEstablished')
    }.property('isLoaded', '_isPollingActive', 'bankConnectionEstablished'),

    startPollingIfNewConnection: function() {
        if (!this.get('_isPollingActive')) {
            this.set('_isPollingActive', true)
            this.set('_lastTotalCount', -1)
            this.set('_pollStartTime', Date.now())
        }

        this.load()
    },

    scheduleNextPoll: function() {
        this.stopPolling()
        if (this.get('_isPollingActive')) {
            this._pollingTimer = Ember.run.later(this, function() {
                this.load()
            }, this.get('pollingInterval'))
        }
    },

    stopPolling: function() {
        if (this._pollingTimer) {
            Ember.run.cancel(this._pollingTimer)
            this._pollingTimer = null
        }
    },

    finishPollingAndReset: function() {
        this.set('_isPollingActive', false)
        this.stopPolling()

        var bankSyncController = this.get('controllers.bankSync')
        if (bankSyncController) {
            bankSyncController.set('bankConnectionEstablished', null)
        }
    },

    evaluatePollingState: function(currentTotalCount) {
        var lastCount = this.get('_lastTotalCount')
        var hasReceivedMatches = lastCount > 0

        // Stop polling if we've hit the maximum matches limit
        if (currentTotalCount >= module.exports.maxMatches) {
            this.finishPollingAndReset()
            return
        }

        // Matches are coming in - keep polling
        if (currentTotalCount > lastCount) {
            this.set('_lastTotalCount', currentTotalCount)
            this.scheduleNextPoll()
            return
        }

        // Matches are not increasing anymore - stop polling
        if (hasReceivedMatches) {
            this.finishPollingAndReset()
            return
        }

        if (!this.get('_pollStartTime')) {
            this.finishPollingAndReset()
            return
        }

        // Retry polling until time limit is reached
        var elapsedTime = Date.now() - this.get('_pollStartTime')
        if (elapsedTime < this.get('_maxPollDuration')) {
            this.scheduleNextPoll()
        } else {
            this.finishPollingAndReset()
        }
    },

    loadMatchSuggestions: function() {
        var self = this
        var api = this.get('api')
        var endpointUrl = '/api/matching-service/' + this.get('organizationId') + '/matches/' + this.get('accountId') + '/bank_lines'
        var bankLinesIds = this.get('bankLinesIds')

        if (!this.get('isBankReconciliationAutoMatchAuthorized')) {
            return Em.RSVP.resolve()
        }

        if (!this.get('isLoaded') || !this.get('bankLinesIds')) {
            return Em.RSVP.resolve()
        }

        if (bankLinesIds.length) {
            return new Em.RSVP.Promise(function(resolve, reject) {
                api.request('POST', endpointUrl, { accept: 'application/json', payload: { bank_line_ids: bankLinesIds } })
                    .then(function(payload) {
                        self.set('matchSuggestions', _.toArray(payload.suggestions).filter(function(suggestion) {
                            var transaction = suggestion.transactions[0]
                            var reconcilable = suggestion.reconcilables[0]

                            if (!transaction || !reconcilable) {
                                return false
                            }

                            if (!transaction.match_id || reconcilable.state === 'draft') {
                                return false
                            }

                            // Suppress non-exact suggestions for cash-based items.
                            var isCrossCurrency = transaction.currency !== reconcilable.currency
                            if (!isCrossCurrency) {
                                var isExactAmount = Math.abs(transaction.amount) - Math.abs(reconcilable.amount) === 0
                                if (!isExactAmount) {
                                    // Try a synchronous store peek — only filter when the record is already loaded
                                    var emberTypeMap = { bill: Billy.Bill, externalInvoice: Billy.ExternalInvoice, invoice: Billy.Invoice }
                                    var ModelClass = emberTypeMap[reconcilable.type]
                                    var itemId = reconcilable.id.split(':')[1]
                                    if (ModelClass && itemId) {
                                        var record = BD.store.recordForTypeAndId(ModelClass, itemId)
                                        if (record && record.get('accountingBasis') === 'cash') {
                                            return false
                                        }
                                    }
                                }
                            }

                            return true
                        }))
                        resolve()
                    })
                    .catch(function(error) {
                        reject(error)
                    })
            })
        }
    }.observes('accountId', 'isLoaded', 'bankLinesIds'),

    load: function() {
        var self = this
        var isPollingActive = this.get('_isPollingActive')

        // Set initial load flag to prevent filter changes during load
        if (!isPollingActive) {
            this.set('_isInitialLoad', true)
        }

        var account = this.get('account')
        var period = this.get('period')
        var sortProperty = this.get('sortProperty') || 'entryDate'
        var sortDirection = this.get('sortDirection') || 'DESC'
        var transactionType = this.get('transactionType')
        var showReconciledTransactions = this.get('showReconciledTransactions')
        var searchQuery = this.get('searchQuery')

        // Track the last request ID so we can ignore results from outdated requests and only show the latest ones
        var requestId = ++this._lastLoadRequestId

        var findProperties = {
            accountId: account.get('id'),
            isApproved: showReconciledTransactions,
            sortProperty: sortProperty,
            sortDirection: sortDirection,
            include: 'bankLineMatch.lines,bankLineMatch.subjectAssociations,bankLineSubjectAssociation.subject',
            pageSize: module.exports.maxMatches
        }

        if (period && period !== 'all') {
            findProperties.entryDatePeriod = period
        } else if (!showReconciledTransactions && this.get('bankAccounts.isLoaded')) {
            var bankAccounts = this.get('bankAccounts.data')
            var bankAccount = bankAccounts.find(function(account) {
                return account.bookkeepingAccountId === findProperties.accountId
            })

            if (bankAccount && bankAccount.reconciliationStartDate) {
                findProperties.entryDatePeriod = 'from:' + bankAccount.reconciliationStartDate
            }
        }

        if (searchQuery) {
            findProperties.q = searchQuery
        }

        if (transactionType === 'positive') {
            findProperties.minAmount = 0
        }

        if (transactionType === 'negative') {
            findProperties.maxAmount = 0
        }

        if (!isPollingActive || this.get('content') === null) {
            this.set('remoteHasMore', false)
                .set('content', null)
                .set('isLoaded', false)
                .set('editMode', false)
                .set('selectedLines', [])
        }

        return new Em.RSVP.Promise(function(resolve, reject) {
            // Only load nextVoucherNo if not showing approved matches, as it's relevant for reconciliation
            var promises = [Billy.BankLineMatch.find(findProperties).promise]
            if (!showReconciledTransactions && !isPollingActive) {
                promises.push(self.get('controllers.nextVoucherNo').load())
            }

            Em.RSVP.all(promises)
                .then(function(values) {
                    if (requestId !== self._lastLoadRequestId) {
                        resolve([])
                        return
                    }

                    var matchesRecordArray = values[0]
                    var matches = matchesRecordArray.get('content').copy() // we just want the raw array, not the BD.RecordArray
                    var currentTotalCount = matchesRecordArray.get('paging.total')

                    if (isPollingActive) {
                        self.evaluatePollingState(currentTotalCount)
                    }

                    var model = Em.ArrayController.create({
                        container: self.container,
                        parentController: self,
                        model: matches,
                        itemController: 'bank-sync-match'
                    })
                    // bankLinesIds relevant only for unreconciled suggestions
                    if (showReconciledTransactions) {
                        self.set('bankLinesIds', null) // Clear if showing approved
                    } else {
                        self.set('bankLinesIds', _.flatten(matches.map(function(match) { return match.get('lines.ids') })))
                    }
                    self.set('content', model)
                        .set('isLoaded', true)
                        .set('remoteHasMore', matches.get('length') < matchesRecordArray.get('paging.total'))
                        .set('_isInitialLoad', false)
                    matchesRecordArray.destroy()

                    resolve(matches)
                }, function() {
                    if (requestId !== self._lastLoadRequestId) return

                    self.container.lookup('util:notification').warn(NOTIFICATION_KEYS.BANK_SYNC_LOAD, t('util.request.default_error'))
                    self.set('isLoaded', true)
                        .set('_isInitialLoad', false)

                    if (isPollingActive) {
                        self.set('_isPollingActive', false)
                        self.stopPolling()
                    }

                    reject()
                })
        })
    }.observes('bankAccounts.isLoaded'),

    filtersChanged: function() {
        if (!this.get('_isPollingActive') && !this.get('_isInitialLoad')) {
            Ember.run.once(this, 'load')
        }
    }.observes(
        'sortProperty',
        'sortDirection',
        'period',
        'showReconciledTransactions',
        'transactionType',
        'searchQuery'
    ),

    isBankSyncReadAuthorized: function() {
        return this.get('auth').isAuthorized(Scope.BankSyncRead)
    }.property(),

    isBankReconciliationAutoMatchAuthorized: function() {
        return this.get('auth').isAuthorized(Scope.BankReconciliationAutoMatch)
    }.property(),

    lineCount: function() {
        var model = this.get('model')
        if (!model) {
            return 0
        }
        return model.reduce(function(result, match) {
            return result + match.get('lineCount')
        }, 0)
    }.property('model.@each.lineCount'),

    showEditButton: function() {
        return this.get('lineCount') > 0 && !this.get('hasPartnerBankConnection')
    }.property('lineCount', 'hasPartnerBankConnection'),

    checkLoadMore: function() {
        // If the remote is empty or this page is not empty, then we don't need to load any more
        if (!this.get('remoteHasMore') || this.get('model.length') > 0) {
            return
        }

        // If the controller is currently saving/deleting something, we set the template into loading mode, but we do not load anything yet
        if (this.get('isManipulating')) {
            this.set('isLoaded', false)
            return
        }

        this.load()
    }.observes('model.length'),

    addMatch: function(match) {
        this.get('model.model').addObject(match)
    },

    removeMatch: function(match, keepSelectedLines) {
        var self = this
        if (keepSelectedLines !== true) {
            match.get('lines').forEach(function(line) {
                self.send('deselectLine', line)
            })
        }
        this.get('model.model').removeObject(match)
    },

    unapproveMatch: function(match) {
        var self = this
        var model = this.get('content.model')

        match.set('isApproved', false)

        return match.save({ headers: getBankMatchTrackingHeaders() })
            .then(function() {
                self.get('segment').track('XXX - Reconciliation Match Canceled (Client)')
                model.removeObject(match)
            }, function(e) {
                self.container.lookup('util:notification').warn(NOTIFICATION_KEYS.BANK_LINES_MATCH_UNAPPROVE, e.message)
                throw e
            })
    },

    formattedLineCount: function() {
        return this.get('lineCount') + (this.get('remoteHasMore') ? '+' : '')
    }.property('lineCount'),

    editMode: false,

    selectedLines: null,

    selectableLineCount: function() {
        var model = this.get('model')
        if (!model) {
            return 0
        }

        return model.reduce(function(result, match) {
            return result + match.get('lines').reduce(function(lineResult, line) {
                return lineResult + (line.get('provider') !== BankLineProvider.bankTransactionService ? 1 : 0)
            }, 0)
        }, 0)
    }.property('model.@each.lines.@each.provider'),

    areAllSelected: function() {
        return this.get('selectableLineCount') > 0 && this.get('selectableLineCount') === this.get('selectedLines.length')
    }.property('selectableLineCount', 'selectedLines.length'),

    areSomeSelected: function() {
        var selectedLinesLength = this.get('selectedLines.length')
        return selectedLinesLength > 0 && this.get('selectableLineCount') > selectedLinesLength
    }.property('selectableLineCount', 'selectedLines.length'),

    entryDateRange: function() {
        var period = this.get('period')

        if (!period || period === 'all') {
            return 'all'
        }

        return datePeriodToRange(period, {
            firstFiscalYearStart: this.get('account.organization.firstFiscalYearStart'),
            firstFiscalYearEnd: this.get('account.organization.firstFiscalYearEnd')
        })
    }.property('period', 'account.organization.firstFiscalYearStart', 'account.organization.firstFiscalYearEnd'),

    actions: {
        downloadBankLines: function() {
            var self = this
            var api = this.get('api')
            var organizationId = this.get('account.organization.id')
            var accountId = this.get('account.id')
            var period = this.get('entryDateRange')

            var queryParams = {
                accountId: accountId,
                isReconciled: false
            }

            var trackingPeriod = period
            if (period !== 'all') {
                var startDate = moment(period.start).format('YYYY-MM-DD')
                var endDate = moment(period.end).format('YYYY-MM-DD')
                queryParams.minEntryDate = startDate
                queryParams.maxEntryDate = endDate
                trackingPeriod = 'dates:' + startDate + '...' + endDate
            }

            api.downloadFile('/organizations/' + organizationId + '/bankLines/download', {
                queryParams: queryParams,
                accept: 'text/csv'
            })
                .then(function(blob) {
                    // Create download link
                    var downloadUrl = window.URL.createObjectURL(blob)
                    var link = document.createElement('a')
                    link.href = downloadUrl
                    link.download = 'bank-lines.csv'
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    setTimeout(function() {
                        window.URL.revokeObjectURL(downloadUrl)
                    }, 200)

                    self.get('segment').track('XXX - Mikkel - Exports', {
                        export_type: 'Bank lines',
                        file_format: 'csv',
                        period: trackingPeriod
                    })
                })
                .catch(function(error) {
                    if (error.code === 'BANK_LINES_CSV_EXPORT_LIMIT_EXCEEDED') {
                        self.container.lookup('util:notification').warn(
                            NOTIFICATION_KEYS.BANK_SYNC_DOWNLOAD_ERROR,
                            t('bank_sync.download_too_many_lines')
                        )
                    } else {
                        self.container.lookup('util:notification').warn(
                            NOTIFICATION_KEYS.BANK_SYNC_DOWNLOAD_ERROR,
                            t('util.request.default_error')
                        )
                    }
                })
        },

        toggleEditMode: function() {
            if (this.get('editMode')) {
                this.set('selectedLines', [])
                this.set('editMode', false)
            } else {
                this.set('editMode', true)
                this.get('segment').track('XXX - Reconciliation Bank Entry Line Edited (Client)')
            }
        },

        toggleSelectAll: function() {
            if (this.get('selectedLines.length')) {
                this.set('selectedLines', [])
            } else {
                var all = this.get('model').mapBy('lines').reduce(function(result, lines) {
                    lines.forEach(function(line) {
                        if (line.get('provider') !== BankLineProvider.bankTransactionService) {
                            result.push(line.get('model'))
                        }
                    })
                    return result
                }, [])
                this.get('selectedLines').addObjects(all)
            }
        },

        selectLine: function(line) {
            this.get('selectedLines').addObject(line)
        },

        deselectLine: function(line) {
            this.get('selectedLines').removeObject(line)
        },

        deleteSelectedLines: function() {
            var self = this
            var possibleSubjectsController = this.get('controllers.bankSyncPossibleSubjects')
            this.container.lookup('util:dialog').confirmWarning(null, t('bank_sync.confirm_delete_postings'), t('bank_sync.confirm_delete_postings_yes'), t('cancel'))
                .then(function() {
                    self.set('isManipulating', true)
                    var selectedLines = self.get('selectedLines')
                    var matches = []
                    var subjects = []

                    // Reset the selection
                    self.set('selectedLines', [])

                    // Remove all matches that will be empty afterwards
                    selectedLines.forEach(function(line) {
                        var match = line.get('match')
                        matches.push(match)
                        if (match.get('lines').every(selectedLines.contains.bind(selectedLines))) {
                            self.removeMatch(match)
                            subjects = subjects.concat(match.get('subjectAssociations').mapBy('subject'))
                        }
                    })

                    // Add subjects to possible subjects since they're now free for grabs
                    subjects.forEach(possibleSubjectsController.addSubject.bind(possibleSubjectsController))

                    // Uniq'ify the matches array since multiple lines might belong to the same match
                    matches.uniq()

                    // If the page is now empty we disable editMode
                    if (self.get('model.length') === 0) {
                        self.set('editMode', false)
                    }

                    BD.deleteRecords(selectedLines, { headers: getBankMatchTrackingHeaders(BANK_MATCH_UI_FLOW.MATCHED) })
                        .success(function() {
                            // Forget about matches that was deleted in other ways. As of writing this, the server automatically deletes matches that do not have any lines. This behavior might change in the future, and the following is also necessary for tests.
                            matches = matches.filterBy('isDestroying', false)

                            // Delete the matches, too
                            BD.deleteRecords(matches)
                                .on('complete', function() {
                                    // Stop the delete sequence
                                    self.set('isManipulating', false)

                                    // Load more in case we deleted all the lines on the page
                                    self.checkLoadMore()
                                })
                        })
                        .error(function() {
                            self.container.lookup('util:notification').warn(NOTIFICATION_KEYS.BANK_SYNC_LINE_DELETE, t('bank_sync.delete_failed'))

                            // If the delete request failed, we need to re-add the matches
                            selectedLines.forEach(function(line) {
                                self.addMatch(line.get('match'))
                            })

                            // And remove the subjects from possible subjects again
                            subjects.forEach(possibleSubjectsController.removeSubject.bind(possibleSubjectsController))

                            // Re-add the lines to the selection
                            self.get('selectedLines').addObjects(selectedLines)
                            self.set('editMode', true)

                            // If checkLoadMore() set this flag to false, we have to set it back to true so the model is shown
                            self.set('isLoaded', true)

                            // Stop the delete sequence
                            self.set('isManipulating', false)
                        })
                })
        }
    },

    customActions: {
        emptyViewButtonClick: function() {
            this.transitionToRoute('bank_import')
        },

        updateBankMatchesFilters: function(e) {
            var controller = this.get('controllers.bankSync')
            var filters = e.detail.filters || {}

            controller.setProperties({
                period: filters.period,
                transactionType: filters.transactionType,
                sortProperty: filters.sortProperty,
                sortDirection: filters.sortDirection,
                searchQuery: filters.searchQuery
            })
        },

        removeMatches: function(e) {
            var self = this
            var matches = e.detail.matches || {}
            var currentSuggestions = self.get('matchSuggestions') || []

            matches.forEach(function(matchId) {
                var match = self.get('model.model').findBy('id', matchId)
                if (match) {
                    self.removeMatch(match)
                }
            })

            var updatedSuggestions = currentSuggestions.filter(function(suggestion) {
                var transaction = suggestion.transactions[0]
                return !matches.includes(transaction.match_id)
            })

            self.set('matchSuggestions', updatedSuggestions)
        }
    }
})

module.exports.maxMatches = 200
